import * as Sentry from '@sentry/react';
import Environment from './environment';

class SentryService {
  private static instance: SentryService;
  private isInitialized = false;
  private environment: Environment;

  private constructor() {
    this.environment = Environment.getInstance();
  }

  public static getInstance(): SentryService {
    if (!SentryService.instance) {
      SentryService.instance = new SentryService();
    }
    return SentryService.instance;
  }

  public init(): void {
    if (this.isInitialized) {
      return;
    }

    // Check if Sentry is properly configured
    const dsn = this.environment.getSentryDsn();
    const isProduction = this.environment.isProduction();

    if (!dsn && isProduction) {
      console.warn(
        '🚨 SENTRY WARNING: Sentry DSN is not configured in production! ' +
        'Errors will not be captured and reported. Please check Environment.getSentryDsn() configuration.'
      );
    } else if (!dsn) {
      console.warn(
        '⚠️ SENTRY INFO: Sentry is not configured. To enable error tracking, ' +
        'configure getSentryDsn() in Environment service.'
      );
    }

    // Only initialize if we have a DSN
    if (dsn) {
      Sentry.init({
        dsn: dsn,

        // Set environment
        environment: this.environment.getSentryEnvironment(),

        // Set release version - we can add this to Environment if needed
        release: this.environment.getAppVersion(),

        // Configure beforeSend to filter out certain errors
        beforeSend(event, hint) {
          // Filter out certain types of errors if needed
          if (hint.originalException instanceof Error) {
            // Filter out network errors that might be too noisy
            if (hint.originalException.message.includes('Network Error')) {
              return null;
            }
          }
          return event;
        },

        // Configure beforeBreadcrumb to filter out certain breadcrumbs
        beforeBreadcrumb(breadcrumb) {
          // Filter out certain types of breadcrumbs if needed
          if (breadcrumb.category === 'console') {
            return null;
          }
          return breadcrumb;
        },
      });

      this.isInitialized = true;
      console.log('✅ Sentry initialized successfully with DSN');
    } else {
      console.warn('⚠️ Sentry not initialized - no DSN configured');
    }
  }

  public captureError(error: Error | string, context?: Record<string, unknown>): void {
    if (this.isInitialized) {
      Sentry.captureException(error, {
        extra: context,
      });
    }
    // REMOVED: console.error to prevent infinite loops with global error handler
    // if (process.env.NODE_ENV === 'development') {
    //   console.error('Error captured:', error, context);
    // }
  }

  public captureMessage(message: string, level: Sentry.SeverityLevel = 'info'): void {
    if (this.isInitialized) {
      Sentry.captureMessage(message, level);
    }
    // REMOVED: console.log to prevent infinite loops with global error handler
    // if (process.env.NODE_ENV === 'development') {
    //   console.log(`[${level.toUpperCase()}] ${message}`);
    // }
  }

  public setUserContext(user: { id: string; email?: string; username?: string }): void {
    if (this.isInitialized) {
      Sentry.setUser(user);
    }
  }

  public clearUserContext(): void {
    if (this.isInitialized) {
      Sentry.setUser(null);
    }
  }

  public getSentryInstance(): typeof Sentry {
    return Sentry;
  }

  // Check if Sentry is properly configured and functional
  public isProperlyConfigured(): boolean {
    const dsn = this.environment.getSentryDsn();
    return this.isInitialized && !!dsn;
  }

  // Get configuration status for debugging
  public getConfigurationStatus(): {
    isInitialized: boolean;
    hasDsn: boolean;
    isProduction: boolean;
    isExplicitlyEnabled: boolean;
    isProperlyConfigured: boolean;
  } {
    const dsn = this.environment.getSentryDsn();
    const isProduction = this.environment.isProduction();
    const isExplicitlyEnabled = this.environment.isSentryEnabled();

    return {
      isInitialized: this.isInitialized,
      hasDsn: !!dsn,
      isProduction,
      isExplicitlyEnabled,
      isProperlyConfigured: this.isProperlyConfigured(),
    };
  }
}

// Export singleton instance
export const sentryService = SentryService.getInstance();

// Export individual methods for backward compatibility
export const initSentry = () => sentryService.init();
export const captureError = (error: Error | string, context?: Record<string, unknown>) =>
  sentryService.captureError(error, context);
export const captureMessage = (message: string, level: Sentry.SeverityLevel = 'info') =>
  sentryService.captureMessage(message, level);
export const setUserContext = (user: { id: string; email?: string; username?: string }) =>
  sentryService.setUserContext(user);
export const clearUserContext = () => sentryService.clearUserContext();
